import { useState } from "react";
import { toast } from "sonner";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useSite } from "./SiteContext";
import { waLink } from "../seo";
import { sendEnquiry } from "../lib/forms";

const BIZ = ["Hotel", "Restaurant", "Caterer", "Distributor", "Exporter", "Other"];
const EMPTY = { name: "", company: "", business_type: "", phone: "", email: "", city: "", message: "" };

export function EnquiryFormFields({ form, setForm, prefix }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="enq-fields">
      <div className="enq-row">
        <label><span>Name *</span><input required autoComplete="name" value={form.name} onChange={set("name")} data-testid={`${prefix}-name`} /></label>
        <label><span>Company</span><input autoComplete="organization" value={form.company} onChange={set("company")} data-testid={`${prefix}-company`} /></label>
      </div>
      <div className="enq-row">
        <label><span>Business type</span>
          <select value={form.business_type} onChange={set("business_type")} data-testid={`${prefix}-biz`}>
            <option value="">Select…</option>
            {BIZ.map((b) => <option key={b} value={b.toLowerCase()}>{b}</option>)}
          </select>
        </label>
        <label><span>City</span><input autoComplete="address-level2" value={form.city} onChange={set("city")} data-testid={`${prefix}-city`} /></label>
      </div>
      <div className="enq-row">
        <label><span>Phone / WhatsApp *</span><input required type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} data-testid={`${prefix}-phone`} /></label>
        <label><span>Email</span><input type="email" autoComplete="email" value={form.email} onChange={set("email")} data-testid={`${prefix}-email`} /></label>
      </div>
      <label><span>Message</span><textarea rows={3} value={form.message} onChange={set("message")} placeholder="Quantities, sizes, finishes, destination…" data-testid={`${prefix}-message`} /></label>
    </div>
  );
}

export function submitEnquiry(form, items) {
  return sendEnquiry(form, items);
}

export default function EnquiryPanel() {
  const { items, setQty, removeItem, clear, panelOpen, setPanelOpen, count } = useSite();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await submitEnquiry(form, items.map((i) => ({ code: i.code, name: i.name, qty: i.qty })));
      toast.success("Enquiry sent", { description: "We reply with a quotation. Your list has been cleared." });
      clear();
      setForm(EMPTY);
      setPanelOpen(false);
    } catch {
      toast.error("Could not send — please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const sendWhatsApp = () => {
    const lines = items.map((i) => `• ${i.name} (${i.code}) × ${i.qty}`).join("\n");
    window.open(waLink(`Hi Sepla, I'd like a quote for:\n${lines}\n\nName: ${form.name || "-"}\nCompany: ${form.company || "-"}`), "_blank");
  };

  return (
    <div className={`enq-panel-wrap ${panelOpen ? "open" : ""}`} data-testid="enquiry-panel">
      <div className="enq-backdrop" onClick={() => setPanelOpen(false)} />
      <aside className="enq-panel">
        <div className="enq-head">
          <h3>Enquiry list</h3>
          <span className="enq-count-mono">{count} item{count === 1 ? "" : "s"}</span>
          <button onClick={() => setPanelOpen(false)} aria-label="Close enquiry list" data-testid="enquiry-close"><X size={20} /></button>
        </div>
        {items.length === 0 ? (
          <p className="enq-empty" data-testid="enquiry-empty">No products yet. Browse the range and tap “+ Add to enquiry”.</p>
        ) : (
          <>
            <ul className="enq-items" data-testid="enquiry-items">
              {items.map((i) => (
                <li key={i.code} className="enq-item" data-testid={`enquiry-item-${i.code}`}>
                  <img src={i.image} alt={i.name} />
                  <div className="enq-item-info">
                    <p>{i.name}</p>
                    <span>{i.code}</span>
                  </div>
                  <div className="enq-qty">
                    <button onClick={() => setQty(i.code, i.qty - 1)} aria-label="Decrease" data-testid={`qty-minus-${i.code}`}><Minus size={13} /></button>
                    <b>{i.qty}</b>
                    <button onClick={() => setQty(i.code, i.qty + 1)} aria-label="Increase" data-testid={`qty-plus-${i.code}`}><Plus size={13} /></button>
                  </div>
                  <button className="enq-del" onClick={() => removeItem(i.code)} aria-label="Remove" data-testid={`remove-${i.code}`}><Trash2 size={14} /></button>
                </li>
              ))}
            </ul>
            <form onSubmit={submit} className="enq-form" data-testid="enquiry-form">
              <EnquiryFormFields form={form} setForm={setForm} prefix="enquiry" />
              <button className="btn btn-accent enq-submit" type="submit" disabled={sending} data-testid="enquiry-submit">
                {sending ? "Sending…" : "Submit quote request"}
              </button>
              <button type="button" className="btn btn-whatsapp enq-wa" onClick={sendWhatsApp} data-testid="enquiry-whatsapp">
                <FaWhatsapp size={16} /> Send list on WhatsApp
              </button>
            </form>
          </>
        )}
      </aside>
    </div>
  );
}

/* Thumbnails flying into the enquiry icon */
export function FlyLayer() {
  const { flies } = useSite();
  return (
    <>
      {flies.map((f) => (
        <img
          key={f.id}
          src={f.src}
          alt=""
          aria-hidden="true"
          className="fly-thumb"
          style={{
            left: f.from.x, top: f.from.y, width: f.from.w, height: f.from.h,
            "--tx": `${f.to.x - f.from.x - f.from.w / 2}px`,
            "--ty": `${f.to.y - f.from.y - f.from.h / 2}px`,
          }}
        />
      ))}
    </>
  );
}
